import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  fetchPublicTournamentLeaderboard,
  fetchPublicTournamentOnlineUsers,
} from '../../../services/tournamentService';
import TournamentPerformanceTabs from '../components/TournamentPerformanceTabs';
import PageShell from '../../../components/layout/PageShell';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const PUBLIC_VIEWER_STORAGE_KEY = 'crickarena-public-viewer-id';

const getPublicViewerId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const storedId = window.localStorage.getItem(PUBLIC_VIEWER_STORAGE_KEY);
  if (storedId) {
    return storedId;
  }

  const generatedId =
    window.crypto?.randomUUID?.() || `viewer-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.localStorage.setItem(PUBLIC_VIEWER_STORAGE_KEY, generatedId);
  return generatedId;
};

const PublicTournamentLeaderboardPage = () => {
  const { tournamentId } = useParams();
  const location = useLocation();
  const matchId = location.state?.matchId;
  const [tournament, setTournament] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [summary, setSummary] = useState(null);
  const [leaders, setLeaders] = useState(null);
  const [performanceLeaders, setPerformanceLeaders] = useState(null);
  const [error, setError] = useState('');
  const [onlineCount, setOnlineCount] = useState(null);
  const [onlineError, setOnlineError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadLeaderboard = async () => {
      if (!tournamentId) return;
      setIsLoading(true);
      setError('');

      try {
        console.log('TOURNAMENT_ID', tournamentId);
        const response = await fetchPublicTournamentLeaderboard(tournamentId);
        console.log('LEADERBOARD_API_RESPONSE', response);
        const data = response?.data || response;
        console.log('LEADERBOARD_DATA', data);
        setTournament(data.tournament || null);
        setLeaderboard(data.leaderboard || []);
        setSummary(data.summary || null);
        setLeaders(data.leaders || null);
        setPerformanceLeaders(data.leaders?.performanceLeaders || null);
      } catch (loadError) {
        setTournament(null);
        setLeaderboard([]);
        setSummary(null);
        setLeaders(null);
        setPerformanceLeaders(null);
        setError(loadError.message || 'Unable to load leaderboard.');
      } finally {
        setIsLoading(false);
      }
    };

    loadLeaderboard();
  }, [tournamentId]);

  useEffect(() => {
    if (!tournamentId) return;
    const viewerId = getPublicViewerId();
    let isMounted = true;
    const refreshIntervalMs = 15000;

    const loadOnlineUsers = async () => {
      try {
        const response = await fetchPublicTournamentOnlineUsers(tournamentId, viewerId);
        if (!isMounted) return;
        setOnlineCount(response.count ?? 0);
        setOnlineError('');
      } catch (loadError) {
        if (!isMounted) return;
        setOnlineError(loadError.message || 'Unable to load online viewers.');
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadOnlineUsers();
      }
    };

    loadOnlineUsers();
    const interval = setInterval(loadOnlineUsers, refreshIntervalMs);
    window.addEventListener('focus', loadOnlineUsers);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', loadOnlineUsers);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [tournamentId]);

  return (
    <PageShell containerClassName="p-0">
    <div className="py-4">
      <div className="bg-body-tertiary border rounded-4 p-4 p-lg-5 shadow-sm mb-4">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div>
            <p className="text-uppercase text-primary fw-semibold small mb-2">Public tournament hub</p>
            <h1 className="display-6 mb-2">{tournament?.name || 'Tournament leaderboard'}</h1>
            <p className="text-muted mb-0">
              Follow the standings, points, and net run rate as matches wrap up.
            </p>
          </div>
          <div className="d-flex flex-column align-items-start align-items-lg-end gap-2">
            {tournamentId && matchId ? (
              <Link
                className="btn btn-sm btn-outline-primary"
                to={`/public/tournaments/${tournamentId}/matches/${matchId}`}
              >
                Back to public match center
              </Link>
            ) : null}
            {onlineError ? (
              <p className="text-muted small mb-0">{onlineError}</p>
            ) : (
              <p className="text-muted small mb-0">Online now: <strong>{onlineCount !== null ? onlineCount : '—'}</strong></p>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {isLoading && (
        <div className="alert alert-secondary" role="alert">
          Loading leaderboard...
        </div>
      )}

      {!isLoading && summary && (
        <div className="alert alert-info d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center">
          <span>
            Completed {summary.completedMatches} of {summary.totalMatches} scheduled matches.
          </span>
          <span className="text-muted small">Updated after the latest completed match.</span>
        </div>
      )}

      <TournamentPerformanceTabs leaders={leaders} performanceLeaders={performanceLeaders} teamLeaderboard={leaderboard} />
    </div>
  
    </PageShell>
  );
};

export default PublicTournamentLeaderboardPage;
